import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'urql';

import {
  Card,
  CardBody,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  NakedForm,
  SubmitButton,
  SwitchField,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { useIsMounted } from '@/hooks';
import { Nullable, PlayerId } from '@/types';
import {
  PlayerUpdateGamanzaBlockMutation,
  PlayerUpdateGamanzaBlockMutationVariables,
} from './__generated__/PlayerUpdateGamanzaBlockForm';

const query = graphql`
  query SanityPlayerUpdateGamanzaBlockForm {
    sanityPlayerUpdateGamanzaBlockForm {
      title {
        ...SanityLocaleString
      }
    }
  }
`;

const updateCustomerGamanzaBlockMutation = gql`
  mutation PlayerUpdateGamanzaBlock($playerGlobalId: ID!, $enable: Boolean!) {
    updateCustomerGamanzaBlock(
      playerGlobalId: $playerGlobalId
      enable: $enable
    ) {
      ... on Customer {
        id
        isGamanzaBlocked
      }
    }
  }
`;

const PlayerUpdateGamanzaBlockForm: FC<{
  playerGlobalId: PlayerId;
  isBlocked: boolean;
}> = ({ playerGlobalId, isBlocked }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerUpdateGamanzaBlockFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();
  const form = staticData.sanityPlayerUpdateGamanzaBlockForm;
  const { close } = useModalContext();
  const { t } = useTranslate();

  const [updateGamanzaBlockState, updateGamanzaBlock] = useMutation<
    PlayerUpdateGamanzaBlockMutation,
    PlayerUpdateGamanzaBlockMutationVariables
  >(updateCustomerGamanzaBlockMutation);

  const defaultValues = {
    isGamanzaBlocked: isBlocked,
  };

  const methods = useForm<typeof defaultValues>({ defaultValues });
  const nextValue = methods.watch('isGamanzaBlocked');
  const isUnchanged = nextValue === isBlocked;

  const onSubmit = ({ isGamanzaBlocked }: typeof defaultValues) => {
    updateGamanzaBlock({
      playerGlobalId,
      enable: isGamanzaBlocked,
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }
    });
  };

  if (!form) {
    return null;
  }

  return (
    <Card
      size="md"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3">
          <NakedForm
            methods={methods}
            onSubmit={onSubmit}
            className="grid gap-6"
          >
            <div className="space-y-1">
              <div className="block text-gray-600 text-sm dark:text-gray-200">
                Should Gamanza be blocked?
              </div>
            </div>
            <SwitchField
              title="Gamanza Blocked"
              name="isGamanzaBlocked"
              labelPlacement="above"
              className="col-span-full"
              allowUndefinedState={false}
            />
            <ErrorMessage message={errorMessage} />
            <SubmitButton
              value="Submit"
              disabled={updateGamanzaBlockState.fetching || isUnchanged}
            />
          </NakedForm>
        </div>
      </CardBody>
    </Card>
  );
};

export default PlayerUpdateGamanzaBlockForm;
